import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen, CheckCircle2, Clock, Target, TrendingUp, Play,
  BarChart3, Award, Flame, Calendar, Loader2, Zap, Star
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { AdminLayout } from "../../components/layout/AdminLayout";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  RadarChart, PolarGrid, PolarAngleAxis, Radar,
  AreaChart, Area,
} from "recharts";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const STATUS_COLORS = { completed: "#22c55e", in_progress: "#f59e0b", enrolled: "#94a3b8" };
const CHART_COLORS = ["#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#06b6d4", "#f43f5e"];

const MyProgressPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statDrill, setStatDrill] = useState(null);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const res = await api.get("/my/learning?page=1&page_size=200");
        setData(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchProgress();
  }, []);

  const stats = data?.stats || {};
  const courses = data?.courses || [];
  const inProgress = courses.filter(c => c.status === "in_progress");
  const completed = courses.filter(c => c.status === "completed");
  const notStarted = courses.filter(c => c.status === "enrolled");
  const totalHours = courses.reduce((sum, c) => sum + (c.duration_hours || 0), 0);
  const completedHours = completed.reduce((sum, c) => sum + (c.duration_hours || 0), 0);

  // Derive chart data
  const completionDonut = useMemo(() => [
    { name: "Completed", value: completed.length, fill: STATUS_COLORS.completed },
    { name: "In Progress", value: inProgress.length, fill: STATUS_COLORS.in_progress },
    { name: "Not Started", value: notStarted.length, fill: STATUS_COLORS.enrolled },
  ].filter(d => d.value > 0), [completed, inProgress, notStarted]);

  const categoryData = useMemo(() => {
    const map = {};
    courses.forEach(c => {
      const cat = c.category || "General";
      if (!map[cat]) map[cat] = { name: cat, total: 0, completed: 0, progress: 0 };
      map[cat].total++;
      if (c.status === "completed") map[cat].completed++;
      map[cat].progress += (c.progress || 0);
    });
    return Object.values(map).map(d => ({
      ...d,
      avgProgress: d.total > 0 ? Math.round(d.progress / d.total) : 0,
      completionRate: d.total > 0 ? Math.round(d.completed / d.total * 100) : 0,
    })).sort((a, b) => b.total - a.total).slice(0, 8);
  }, [courses]);

  const radarData = useMemo(() => {
    return categoryData.slice(0, 6).map(d => ({
      category: d.name.length > 12 ? d.name.slice(0, 12) + "..." : d.name,
      proficiency: d.avgProgress,
      fullMark: 100,
    }));
  }, [categoryData]);

  const monthlyActivity = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = d.toISOString().slice(0, 7);
      const label = d.toLocaleDateString("en-US", { month: "short" });
      const enrolled = courses.filter(c => c.enrolled_at?.startsWith(key)).length;
      const completedInMonth = courses.filter(c => c.status === "completed" && c.enrolled_at?.startsWith(key)).length;
      months.push({ month: label, enrolled, completed: completedInMonth });
    }
    return months;
  }, [courses]);

  const streakDays = useMemo(() => {
    if (courses.length === 0) return 0;
    const accessed = courses.filter(c => c.last_accessed_at).map(c => new Date(c.last_accessed_at).toDateString());
    const unique = [...new Set(accessed)].sort((a, b) => new Date(b) - new Date(a));
    let streak = 0;
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      if (unique.includes(d.toDateString())) streak++;
      else if (i > 0) break;
    }
    return streak;
  }, [courses]);

  const content = (
    <div className="space-y-5" data-testid="my-progress-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" data-testid="my-progress-title">My Progress</h1>
        <p className="text-muted-foreground text-sm mt-0.5">Track your learning journey and stay on top of your goals</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <>
          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: "Total Courses", value: stats.total_modules || 0, icon: BookOpen, color: "#3b82f6", bg: "bg-blue-50" },
              { label: "Completed", value: stats.completed || 0, icon: CheckCircle2, color: "#22c55e", bg: "bg-green-50" },
              { label: "In Progress", value: stats.in_progress || 0, icon: Clock, color: "#f59e0b", bg: "bg-amber-50" },
              { label: "Not Started", value: stats.not_started || 0, icon: Target, color: "#94a3b8", bg: "bg-slate-50" },
              { label: "Hours Learned", value: `${completedHours.toFixed(0)}h`, icon: Flame, color: "#f97316", bg: "bg-orange-50" },
              { label: "Day Streak", value: streakDays, icon: Zap, color: "#8b5cf6", bg: "bg-violet-50" },
            ].map((st, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Card className="border shadow-none hover:shadow-sm transition-shadow cursor-pointer" onClick={() => setStatDrill(st)} data-testid={`stat-card-${i}`}>
                  <CardContent className="p-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-9 h-9 rounded-lg ${st.bg} flex items-center justify-center`}>
                        <st.icon className="w-4 h-4" style={{ color: st.color }} />
                      </div>
                      <div>
                        <p className="text-xl font-bold leading-none">{st.value}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{st.label}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Overall Completion Bar */}
          <Card className="border shadow-none">
            <CardContent className="py-4 px-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  <span className="font-semibold text-sm">Overall Completion</span>
                </div>
                <span className="text-xl font-bold text-primary">{stats.completion_rate || 0}%</span>
              </div>
              <Progress value={stats.completion_rate || 0} className="h-2.5" />
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-[10px] text-muted-foreground">{stats.completed || 0} of {stats.total_modules || 0} courses completed</p>
                <p className="text-[10px] text-muted-foreground">{completedHours.toFixed(0)}h of {totalHours.toFixed(0)}h total</p>
              </div>
            </CardContent>
          </Card>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Completion Donut */}
            <Card className="border shadow-none" data-testid="chart-completion-donut">
              <CardHeader className="pb-1 pt-4 px-5">
                <CardTitle className="text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Course Status</CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                {completionDonut.length > 0 ? (
                  <div className="flex items-center gap-3">
                    <ResponsiveContainer width="60%" height={160}>
                      <PieChart>
                        <Pie data={completionDonut} cx="50%" cy="50%" innerRadius={42} outerRadius={65} dataKey="value" paddingAngle={3} strokeWidth={0}>
                          {completionDonut.map((d, i) => <Cell key={i} fill={d.fill} />)}
                        </Pie>
                        <Tooltip formatter={(v, n) => [`${v} courses`, n]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2">
                      {completionDonut.map((d, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.fill }} />
                          <span className="text-xs text-muted-foreground">{d.name}</span>
                          <span className="text-xs font-bold">{d.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-center py-8 text-sm text-muted-foreground">No courses yet</p>
                )}
              </CardContent>
            </Card>

            {/* Category Progress Bar Chart */}
            <Card className="border shadow-none" data-testid="chart-category-progress">
              <CardHeader className="pb-1 pt-4 px-5">
                <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-violet-500" /> Progress by Category</CardTitle>
              </CardHeader>
              <CardContent className="px-2 pb-4">
                {categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={categoryData.slice(0, 6)} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" height={40} />
                      <YAxis tick={{ fontSize: 9 }} domain={[0, 100]} />
                      <Tooltip formatter={(v) => [`${v}%`, "Avg Progress"]} />
                      <Bar dataKey="avgProgress" radius={[4, 4, 0, 0]} maxBarSize={28}>
                        {categoryData.slice(0, 6).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center py-8 text-sm text-muted-foreground">No data</p>
                )}
              </CardContent>
            </Card>

            {/* Skill Radar */}
            <Card className="border shadow-none" data-testid="chart-skill-radar">
              <CardHeader className="pb-1 pt-4 px-5">
                <CardTitle className="text-sm flex items-center gap-2"><Star className="w-4 h-4 text-amber-500" /> Skill Proficiency</CardTitle>
              </CardHeader>
              <CardContent className="px-2 pb-4">
                {radarData.length >= 3 ? (
                  <ResponsiveContainer width="100%" height={160}>
                    <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="category" tick={{ fontSize: 8 }} />
                      <Radar name="Proficiency" dataKey="proficiency" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} strokeWidth={2} />
                      <Tooltip formatter={(v) => [`${v}%`, "Proficiency"]} />
                    </RadarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center py-8 text-sm text-muted-foreground">Need 3+ categories</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Learning Activity Trend */}
          <Card className="border shadow-none" data-testid="chart-activity-trend">
            <CardHeader className="pb-1 pt-4 px-5">
              <CardTitle className="text-sm flex items-center gap-2"><Calendar className="w-4 h-4 text-cyan-500" /> Learning Activity (6 Months)</CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-4">
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={monthlyActivity} margin={{ top: 10, right: 15, left: -15, bottom: 0 }}>
                  <defs>
                    <linearGradient id="enrolledGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="completedGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="enrolled" stroke="#3b82f6" fill="url(#enrolledGrad)" strokeWidth={2} name="Enrolled" />
                  <Area type="monotone" dataKey="completed" stroke="#22c55e" fill="url(#completedGrad)" strokeWidth={2} name="Completed" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* In Progress Courses */}
          {inProgress.length > 0 && (
            <Card className="border shadow-none">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-amber-500" /> Continue Learning ({inProgress.length})</CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4 space-y-2">
                {inProgress.map((c) => (
                  <div
                    key={c.enrollment_id}
                    className="flex items-center gap-3 p-2.5 rounded-lg border hover:border-primary/20 hover:bg-muted/30 transition-all cursor-pointer group"
                    onClick={() => navigate(`/course/${c.course_id}?autostart=true`)}
                    data-testid={`progress-course-${c.course_id}`}
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0">
                      {c.thumbnail_url ? (
                        <img src={c.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                          <BookOpen className="w-4 h-4 text-white/60" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.title}</p>
                      <p className="text-[10px] text-muted-foreground">{c.category} · {c.duration_hours}h</p>
                    </div>
                    <div className="w-28 shrink-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[10px] font-semibold text-amber-500">{c.progress}%</span>
                      </div>
                      <Progress value={c.progress} className="h-1.5" />
                    </div>
                    <Button size="sm" variant="outline" className="gap-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity shrink-0" data-testid={`resume-${c.course_id}`}>
                      <Play className="w-3 h-3" /> Resume
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Category Breakdown Table */}
          {categoryData.length > 0 && (
            <Card className="border shadow-none" data-testid="category-breakdown">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" /> Category Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                <div className="space-y-2.5">
                  {categoryData.map((cat, i) => (
                    <div key={cat.name} className="flex items-center gap-3">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span className="text-sm font-medium w-40 truncate">{cat.name}</span>
                      <div className="flex-1">
                        <Progress value={cat.avgProgress} className="h-2" />
                      </div>
                      <span className="text-xs font-semibold w-10 text-right">{cat.avgProgress}%</span>
                      <Badge variant="outline" className="text-[9px] w-16 justify-center">{cat.completed}/{cat.total}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Completed Courses */}
          {completed.length > 0 && (
            <Card className="border shadow-none">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm flex items-center gap-2"><Award className="w-4 h-4 text-green-500" /> Completed ({completed.length})</CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {completed.map((c) => (
                    <div
                      key={c.enrollment_id}
                      className="flex items-center gap-3 p-2.5 rounded-lg border border-green-500/10 bg-green-500/5 cursor-pointer hover:border-green-500/20 transition-colors"
                      onClick={() => navigate(`/course/${c.course_id}`)}
                      data-testid={`completed-course-${c.course_id}`}
                    >
                      <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{c.title}</p>
                        <p className="text-[10px] text-muted-foreground">{c.category} · {c.duration_hours}h</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Not Started */}
          {notStarted.length > 0 && (
            <Card className="border shadow-none">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm flex items-center gap-2"><Target className="w-4 h-4 text-slate-400" /> Not Started ({notStarted.length})</CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {notStarted.slice(0, 8).map((c) => (
                    <div
                      key={c.enrollment_id}
                      className="flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer hover:border-primary/20 hover:bg-muted/30 transition-all"
                      onClick={() => navigate(`/course/${c.course_id}?autostart=true`)}
                      data-testid={`notstarted-course-${c.course_id}`}
                    >
                      <BookOpen className="w-4 h-4 text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{c.title}</p>
                        <p className="text-[10px] text-muted-foreground">{c.category} · {c.duration_hours}h</p>
                      </div>
                      <Button size="sm" variant="outline" className="text-xs shrink-0" onClick={(e) => { e.stopPropagation(); navigate(`/course/${c.course_id}?autostart=true`); }}>
                        Start
                      </Button>
                    </div>
                  ))}
                  {notStarted.length > 8 && (
                    <p className="text-xs text-muted-foreground text-center col-span-2 pt-1">+{notStarted.length - 8} more courses</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Stat Drill-Down Dialog */}
      <Dialog open={!!statDrill} onOpenChange={() => setStatDrill(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {statDrill?.icon && <statDrill.icon className="w-5 h-5" style={{ color: statDrill?.color }} />}
              {statDrill?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center py-6 bg-muted/20 rounded-xl">
              <p className="text-4xl font-bold">{statDrill?.value}</p>
              <p className="text-sm text-muted-foreground mt-1">{statDrill?.label}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  if (user?.role === "tenant_admin") return <AdminLayout title="My Progress" variant="tenant_admin">{content}</AdminLayout>;
  if (user?.role === "super_admin") return <AdminLayout title="My Progress" variant="super_admin">{content}</AdminLayout>;
  if (user?.role === "manager") return <AdminLayout title="My Progress" variant="manager">{content}</AdminLayout>;
  return <AdminLayout title="My Progress" variant="learner">{content}</AdminLayout>;
};

export default MyProgressPage;
